import React, { useState, useEffect } from 'react';
import { TextField, Popover, Box, Button, Stack } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';

type Props = {
  from: DateTime | null;
  to: DateTime | null;
  onChange: (from: DateTime | null, to: DateTime | null) => void;
};

const formatRange = (from: DateTime | null, to: DateTime | null) => {
  if (!from && !to) return '';
  const f = from?.toFormat('dd/MM/yyyy') || '...';
  const t = to?.toFormat('dd/MM/yyyy') || '...';
  return `${f} - ${t}`;
};

const DateRangePicker = ({ from, to, onChange }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [draftFrom, setDraftFrom] = useState<DateTime | null>(from);
  const [draftTo, setDraftTo] = useState<DateTime | null>(to);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open) {
      setDraftFrom(from);
      setDraftTo(to);
    }
  }, [open, from, to]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-br">
      <TextField
        label="Data"
        size="small"
        fullWidth
        value={formatRange(from, to)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        InputProps={{
          readOnly: true,
          sx: {
            fontSize: 13,
            lineHeight: '16px',
            '& .MuiInputBase-input': {
              padding: '10.5px 8px',
              textAlign: 'center',
            },
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: 2, // rounded
            p: 1.5,
            width: 260, // compact width
          },
        }}
      >
        <Box>
          <Stack spacing={1.5}>
            <DatePicker<DateTime>
              label="De"
              value={draftFrom}
              onChange={(newValue: DateTime | null) => {
                if (newValue?.isValid) setDraftFrom(newValue);
              }}
              maxDate={draftTo || undefined}
              inputFormat="dd/MM/yyyy"
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />

            <DatePicker<DateTime>
              label="Até"
              value={draftTo}
              onChange={(newValue: DateTime | null) => {
                if (newValue?.isValid) setDraftTo(newValue);
              }}
              minDate={draftFrom || undefined}
              inputFormat="dd/MM/yyyy"
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />

            <Stack direction="row" justifyContent="space-between" mt={0.5}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setDraftFrom(null);
                  setDraftTo(null);
                  onChange(null, null);
                  setAnchorEl(null);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Limpar
              </Button>

              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  onChange(draftFrom, draftTo);
                  setAnchorEl(null);
                }}
                sx={{
                  textTransform: 'none',
                }}
              >
                Aplicar
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
